import { PublicClientApplication } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { jwtDecode } from 'jwt-decode'

import environnement from './config/environnement'
import { useMsalContext } from '../application/context/msalContext'
import { AUTH_TYPE } from '../infrastructure/enums/authentication.enum'
import { FetchApiRequest, FetchConfig } from './api/types/fetch.api.type'

const reloadLogin = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('selectedType')
  window.location.reload()
}

const internalTenantConfiguration = {
  auth: {
    clientId: environnement.INTERNAL_FRONT_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${environnement.INTERNAL_TENANT_ID}`,
    redirectUri: environnement.FRONT_REDIRECT_URI,
    postLogoutRedirectUri: environnement.FRONT_REDIRECT_URI,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: any, message: any, containsPii: any) => {
        if (containsPii) {
          return
        }
        switch (level) {
          case 0:
            console.error('Error: ', message)
            if (message.includes('window closed')) {
              reloadLogin()
            }
            return
          default:
            return
        }
      },
    },
  },
}

export const loginRequestInt = {
  scopes: ['openid', 'profile', `api://${environnement.INTERNAL_BACK_CLIENT_ID}/client.api`],
}

const externalTenantConfiguration = {
  auth: {
    clientId: environnement.EXTERNAL_FRONT_CLIENT_ID,
    authority: `https://${environnement.EXTERNAL_DOMAIN}.b2clogin.com/${environnement.EXTERNAL_DOMAIN}.onmicrosoft.com/${environnement.EXTERNAL_SIGNIN_FLOW}`,
    knownAuthorities: [`${environnement.EXTERNAL_DOMAIN}.b2clogin.com`],
    redirectUri: environnement.FRONT_REDIRECT_URI,
    postLogoutRedirectUri: environnement.FRONT_REDIRECT_URI,
  },

  system: {
    loggerOptions: {
      loggerCallback: (level: any, message: any, containsPii: any) => {
        if (containsPii) {
          return
        }
        switch (level) {
          case 0:
            console.error('Error: ', message)
            if (message.includes('window closed')) {
              reloadLogin()
            }
            return
          default:
            return
        }
      },
    },
  },
}

export const loginRequestExt = {
  scopes: [
    'openid',
    'profile',
    `https://${environnement.EXTERNAL_DOMAIN}.onmicrosoft.com/${environnement.EXTERNAL_BACK_CLIENT_ID}/client-back`,
  ],
}

export const initializeMsal = async (authType: AUTH_TYPE) => {
  const config =
    authType === AUTH_TYPE.EXTERNAL ? externalTenantConfiguration : internalTenantConfiguration
  return new PublicClientApplication(config)
}

export const useAuthenticatedApi = () => {
  const { instance, accounts } = useMsal()
  const { loginRequest } = useMsalContext()

  const getAccessToken = async () => {
    const request = {
      ...loginRequest,
      account: accounts[0],
    }
    const response = await instance.acquireTokenSilent(
      request as { scopes: string[]; account: any }
    )
    const accessToken = response.accessToken

    const decodedToken = jwtDecode(accessToken)
    if (!decodedToken) {
      throw new Error('Invalid token')
    }
    localStorage.setItem('token', accessToken)
    return response.accessToken
  }

  const authenticatedApi = async (url: string, options: FetchApiRequest = {}) => {
    const accessToken = await getAccessToken()
    const headers = {
      ...options.headers,
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': options.contentType ?? 'application/json',
      Accept: options.accept ?? 'application/json',
      'Access-Control-Allow-Origin': '*',
    }

    const config: FetchConfig = {
      method: options.method ?? 'GET',
      headers,
    }

    if (options.method === 'POST') {
      config.body = JSON.stringify(options.body)
    }

    return fetch(`${environnement.API_URL}/${url}`, config)
  }
  return authenticatedApi
}
